/**
 * The ID List's rows, as pure functions of version data — no React, no browser.
 *
 * Lifted out of the page (unchanged) so the SERVER can compute the same row counts the client renders:
 * the page count decides each tab's title, whether a `?page=` exists at all, and its robots tag. A
 * second, "equivalent" derivation on the server would drift from the table the user actually sees, so
 * there is exactly one.
 */
import type { GameItem, GameBlock, GameEntity } from '@/app/lib/gameData';
import type { FoodData, GameEnchantment, GameEffect } from '@/app/lib/gameData/types';
import { getModByNamespace } from '@/app/lib/gameData/modRegistry';
import type { DataType } from './idListUrl';

// Entities to exclude (non-gameplay entities or duplicates of items)
export const excludedEntities = new Set([
  'area_effect_cloud',
]);

/**
 * Boats and rafts are vehicles, not mobs — by SHAPE, so a new wood is covered without an edit
 * (26.3's poplar_boat / poplar_chest_boat were listed under Mobs while the names were a list).
 * Matches `boat`, `chest_boat` and every `<wood>_boat` / `_chest_boat` / `_raft` / `_chest_raft`.
 */
const BOAT_ENTITY = /(^|_)(chest_)?(boat|raft)$/;

export function isExcludedEntity(stringId: string): boolean {
  return excludedEntities.has(stringId) || BOAT_ENTITY.test(stringId);
}

/**
 * A block row's stack size is its ITEM's (`max_stack_size`): the block data carries a flat 64
 * (`sync-game-data` wrote it for every block until 2026-10-30), so beds and shulker boxes read 64
 * where the game stacks 1, and signs and banners 64 where it stacks 16. The block's item form is
 * `itemForm` (`wall_torch` → `torch`); a block with no item (fire, water) has no stack size.
 */
function blockStackSize(block: GameBlock, itemByFullId: ReadonlyMap<string, GameItem>): number | undefined {
  const item = itemByFullId.get(block.itemForm ?? block.fullId);
  if (item) return item.stackSize;
  return block.namespace === 'minecraft' ? undefined : block.stackSize;
}

// Unified type for display
export interface DisplayItem {
  numericId?: number | null;
  stringId: string;
  fullId: string;
  displayName: string;
  versionAdded?: string;
  dataType: 'item' | 'block' | 'entity' | 'enchantment' | 'effect';
  /**
   * Whether `/give` can actually take this id — i.e. whether the ITEM registry holds it.
   *
   * 🚨 Not the same as "is a block". ~154 blocks have no item form on 26.2 (`wall_torch`,
   * `nether_portal`, every `potted_*`, every `*_wall_sign`), and `/give @p minecraft:wall_torch`
   * is refused outright. `item.blockId` is NOT a usable proxy either: it is the block an item
   * PLACES, so `string` claims `tripwire`, `redstone` claims `redstone_wire` and
   * `powder_snow_bucket` claims `powder_snow` — 13 non-item blocks look givable through it.
   */
  givable: boolean;
  // Item-specific
  stackSize?: number;
  maxDurability?: number;
  foodPoints?: number;
  food?: FoodData;
  rarity?: string;
  enchantCategories?: string[];
  repairWith?: string[];
  damageResistant?: boolean;
  blockId?: string;
  // Block-specific
  hardness?: number | null;
  transparent?: boolean;
  blastResistance?: number;
  emitLight?: number;
  requiresTool?: boolean;
  material?: string;
  mapColor?: string;
  slipperiness?: number;
  hasBlockEntity?: boolean;
  // Entity-specific
  type?: string;
  width?: number;
  height?: number;
  entityCategory?: string;
  // Enchantment-specific
  maxLevel?: number;
  enchantCategory?: string;
  treasureOnly?: boolean;
  curse?: boolean;
  enchantRarity?: string;
  appliesTo?: string[];
  exclude?: string[];
  // Effect-specific
  effectType?: 'good' | 'bad' | 'neutral';
  effectColor?: string;
  instant?: boolean;
  // Mod support
  namespace?: string;
  modId?: string;
  modDisplayName?: string;
  texturePath?: string;
}

export interface IdListSourceData {
  items: GameItem[];
  blocks: GameBlock[];
  entities: GameEntity[];
  enchantments: GameEnchantment[];
  effects: GameEffect[];
}

export interface IdListAllData {
  allData: {
    items: DisplayItem[];
    blocks: DisplayItem[];
    entities: DisplayItem[];
    enchantments: DisplayItem[];
    effects: DisplayItem[];
  };
  overriddenIds: Set<string>;
  hasModdedContent: boolean;
  availableMods: Map<string, string>;
}

/**
 * Convert all data to unified format.
 * Also computes mod metadata (overrides, modded content, available mods) in one pass.
 */
export function buildIdListData(versionData: IdListSourceData): IdListAllData {
  // Build a map of block data keyed by fullId for enriching items that are also blocks
  const blockMap = new Map<string, GameBlock>();
  for (const block of versionData.blocks) {
    blockMap.set(block.fullId, block);
  }

  // Every id `/give` will accept. Built from the item registry itself, because that is the
  // question the command asks — see `givable` on DisplayItem.
  const itemFullIds = new Set(versionData.items.map(i => i.fullId));
  const itemByFullId = new Map(versionData.items.map(i => [i.fullId, i] as const));

  // Build a map of items keyed by blockId for O(1) block→item lookup
  const itemByBlockId = new Map<string, GameItem>();
  for (const item of versionData.items) {
    if (item.blockId) {
      itemByBlockId.set(item.blockId, item);
    }
  }

  // Track mod metadata in the same pass
  const byStringId = new Map<string, Set<string>>();
  let _hasModdedContent = false;
  const _availableMods = new Map<string, string>();

  const trackMod = (item: DisplayItem) => {
    const ns = item.namespace ?? 'minecraft';
    if (ns !== 'minecraft') {
      _hasModdedContent = true;
      if (!_availableMods.has(ns)) {
        _availableMods.set(ns, item.modDisplayName || ns);
      }
    }
    if (!byStringId.has(item.stringId)) byStringId.set(item.stringId, new Set());
    byStringId.get(item.stringId)!.add(ns);
  };

  const items: DisplayItem[] = versionData.items.map(item => {
    const block = item.blockId ? blockMap.get(item.blockId) : undefined;
    const di: DisplayItem = {
      numericId: item.numericId,
      stringId: item.stringId,
      fullId: item.fullId,
      displayName: item.displayName,
      versionAdded: item.versionAdded,
      dataType: 'item' as const,
      givable: true,                          // it came out of the item registry
      stackSize: item.stackSize,
      maxDurability: item.maxDurability,
      foodPoints: item.food?.foodPoints,
      food: item.food,
      rarity: item.rarity,
      enchantCategories: item.enchantCategories,
      repairWith: item.repairWith,
      damageResistant: item.damageResistant,
      blockId: item.blockId,
      hardness: block?.hardness,
      blastResistance: block?.blastResistance,
      transparent: block?.transparent,
      emitLight: block?.emitLight,
      requiresTool: block?.requiresTool,
      material: block?.material,
      mapColor: block?.mapColor,
      slipperiness: block?.slipperiness,
      hasBlockEntity: block?.hasBlockEntity,
      namespace: item.namespace,
      modId: item.modId,
      modDisplayName: item.modDisplayName,
      texturePath: item.texturePath,
    };
    trackMod(di);
    return di;
  });

  const blocks: DisplayItem[] = versionData.blocks.map(block => {
    // O(1) lookup instead of O(n) find
    const matchingItem = itemByBlockId.get(block.fullId);
    const di: DisplayItem = {
      numericId: block.numericId,
      stringId: block.stringId,
      fullId: block.fullId,
      displayName: block.displayName,
      versionAdded: block.versionAdded,
      dataType: 'block' as const,
      // The id itself has to be in the item registry — see `givable`.
      givable: itemFullIds.has(block.fullId),
      stackSize: blockStackSize(block, itemByFullId),
      hardness: block.hardness,
      transparent: block.transparent,
      blastResistance: block.blastResistance,
      emitLight: block.emitLight,
      requiresTool: block.requiresTool,
      material: block.material,
      mapColor: block.mapColor,
      slipperiness: block.slipperiness,
      hasBlockEntity: block.hasBlockEntity,
      rarity: matchingItem?.rarity,
      enchantCategories: matchingItem?.enchantCategories,
      repairWith: matchingItem?.repairWith,
      damageResistant: matchingItem?.damageResistant,
      maxDurability: matchingItem?.maxDurability,
      food: matchingItem?.food,
      foodPoints: matchingItem?.food?.foodPoints,
      namespace: block.namespace,
      modId: block.modId,
      modDisplayName: block.modDisplayName,
      texturePath: block.texturePath,
    };
    trackMod(di);
    return di;
  });

  const entities: DisplayItem[] = versionData.entities.map(entity => {
    const di: DisplayItem = {
      numericId: entity.numericId,
      stringId: entity.stringId,
      fullId: entity.fullId,
      displayName: entity.displayName,
      versionAdded: entity.versionAdded,
      dataType: 'entity' as const,
      givable: false,
      type: entity.type,
      width: entity.width,
      height: entity.height,
      entityCategory: entity.category,
      namespace: entity.namespace,
      modId: entity.modId,
      modDisplayName: entity.modDisplayName,
      texturePath: entity.texturePath,
    };
    trackMod(di);
    return di;
  });

  const enchantments: DisplayItem[] = versionData.enchantments.map(ench => {
    const mod = ench.namespace && ench.namespace !== 'minecraft' ? getModByNamespace(ench.namespace) : undefined;
    const di: DisplayItem = {
      stringId: ench.stringId,
      fullId: ench.fullId,
      displayName: ench.displayName,
      dataType: 'enchantment' as const,
      givable: false,
      namespace: ench.namespace,
      modDisplayName: mod?.displayName,
      maxLevel: ench.maxLevel,
      enchantCategory: ench.category,
      treasureOnly: ench.treasureOnly,
      curse: ench.curse,
      enchantRarity: ench.rarity,
      appliesTo: ench.supportedItems ?? ench.appliesTo,
      exclude: ench.exclusiveWith ?? ench.exclude,
    };
    trackMod(di);
    return di;
  });

  const effects: DisplayItem[] = versionData.effects.map(effect => {
    const mod = effect.namespace && effect.namespace !== 'minecraft' ? getModByNamespace(effect.namespace) : undefined;
    const di: DisplayItem = {
      stringId: effect.stringId,
      fullId: effect.fullId,
      displayName: effect.displayName,
      dataType: 'effect' as const,
      givable: false,
      namespace: effect.namespace,
      modDisplayName: mod?.displayName,
      effectType: effect.type,
      effectColor: effect.color,
      instant: effect.instant,
    };
    trackMod(di);
    return di;
  });

  // Compute overridden IDs
  const _overriddenIds = new Set<string>();
  for (const [stringId, namespaces] of byStringId) {
    if (namespaces.size > 1) _overriddenIds.add(stringId);
  }

  return {
    allData: { items, blocks, entities, enchantments, effects },
    overriddenIds: _overriddenIds,
    hasModdedContent: _hasModdedContent,
    availableMods: _availableMods,
  };
}

/** The rows a tab shows before any source filter, search or sort. */
export function rowsForType(allData: IdListAllData['allData'], dataType: DataType): DisplayItem[] {
  // Filter entities to exclude non-gameplay ones
  const filteredEntities = allData.entities.filter(e => !isExcludedEntity(e.stringId));

  switch (dataType) {
    case 'items': {
      // Items that are NOT also blocks (pure items like swords, tools, etc.)
      // Use fullId so mod items aren't excluded by vanilla blocks with the same stringId
      const blockFullIds = new Set(allData.blocks.map(b => b.fullId));
      return allData.items.filter(item => !blockFullIds.has(item.fullId));
    }
    case 'blocks':
      return allData.blocks;
    case 'mobs':
      return filteredEntities;
    case 'enchantments':
      return allData.enchantments;
    case 'effects':
      return allData.effects;
    case 'all':
    default: {
      // Deduplicate: blocks take priority, then items not in blocks, then entities
      // Use fullId so mod entries with the same stringId as vanilla still appear
      const seenIds = new Set<string>();
      const combined: DisplayItem[] = [];

      // Add all blocks first
      for (const block of allData.blocks) {
        if (!seenIds.has(block.fullId)) {
          seenIds.add(block.fullId);
          combined.push(block);
        }
      }

      // Add items that aren't already in blocks
      for (const item of allData.items) {
        if (!seenIds.has(item.fullId)) {
          seenIds.add(item.fullId);
          combined.push(item);
        }
      }

      // Add filtered entities (excluding area_effect_cloud and boat entities)
      for (const entity of filteredEntities) {
        if (!seenIds.has(entity.fullId)) {
          seenIds.add(entity.fullId);
          combined.push(entity);
        }
      }

      // Add enchantments and effects
      for (const ench of allData.enchantments) {
        combined.push(ench);
      }
      for (const effect of allData.effects) {
        combined.push(effect);
      }

      return combined;
    }
  }
}
